/**
 * Page scaffolding for content rendered inside the shell.
 *
 *  <Page>                      – consistent canvas padding
 *    <PageHeader title="…" subtitle="…" actions={<Button/>} />
 *    <Panel>…</Panel>          – white bordered panel (same chrome as DataGrid)
 *  </Page>
 */
import React from 'react';

export function Page({ children, className = '', ...rest }) {
  return (
    <div className={`p-4 lg:p-5 space-y-4 ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
      <div>
        <h1 className="font-heading text-xl lg:text-2xl font-bold text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {(actions || children) && <div className="flex items-center gap-2 flex-wrap">{actions}{children}</div>}
    </div>
  );
}

export function Panel({ title, actions, children, className = '', bodyClassName = '' }) {
  return (
    <div className={`bg-white border border-border rounded-lg shadow-sm ${className}`}>
      {(title || actions) && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          {title && <h2 className="font-heading text-base font-semibold mr-auto">{title}</h2>}
          {actions}
        </div>
      )}
      <div className={bodyClassName || 'p-4'}>{children}</div>
    </div>
  );
}

/** Compact KPI tile row used above grids (Total / Active / …). */
export function StatTiles({ items = [] }) {
  if (!items.length) return null;
  return (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <div key={it.label} className="bg-white border border-border rounded-lg px-4 py-3 flex items-center gap-3" data-testid={it.testid}>
            {Icon && (
              <div className={`w-9 h-9 rounded-md flex items-center justify-center ${it.iconClass || 'bg-shell-primary/10 text-shell-primary'}`}>
                <Icon className="w-5 h-5" />
              </div>
            )}
            <div className="min-w-0">
              {/* Two columns on a phone leaves ~130px for a label: let it take a second
                  line rather than become "TEACHER ACCU…". One line again from sm up. */}
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground leading-tight break-words sm:truncate">{it.label}</div>
              <div className="text-xl font-semibold leading-tight tabular-nums">{it.value ?? '—'}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
